import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, FileText, CreditCard } from "lucide-react";

const CancellationsRefunds = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-start items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 md:py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mb-4 sm:mb-6">
            <RefreshCw className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Cancellations & Refunds Policy
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Last Updated: October 9, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 sm:py-12 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 sm:p-8 md:p-10">
            <div className="prose prose-sm sm:prose-base max-w-none">
              <div className="space-y-6 sm:space-y-8 text-gray-700 leading-relaxed">

                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    Overview
                  </h2>
                  <p className="text-sm sm:text-base mb-4">
                    This Cancellations & Refunds Policy outlines our terms for subscription cancellations and refunds.
                    This policy is in compliance with the Consumer Protection Act, 2019, and other applicable Indian laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">1. Trials & Demos</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Free Trials:</p>
                      <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                        <li>7-day free trial providing full or limited access to our Service</li>
                        <li>Auto-convert to paid plans unless cancelled before renewal</li>
                        <li>No charges during the trial period</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Demos:</p>
                      <p className="text-sm sm:text-base ml-2">
                        Complimentary product demonstrations; no charges unless you agree to a paid plan.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    2. Subscriptions & Renewals
                  </h3>
                  <div className="space-y-3">
                    <p className="text-sm sm:text-base"><strong>Payment:</strong> All plans are prepaid (monthly or annual).</p>
                    <p className="text-sm sm:text-base"><strong>Auto-renewal:</strong> Plans renew automatically at the end of each term unless cancelled.</p>
                    <p className="text-sm sm:text-base"><strong>Cancellation:</strong></p>
                    <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                      <li>Can be done anytime via the billing page or by contacting support</li>
                      <li>Stops future renewals but keeps access until the end of the current term</li>
                      <li>No cancellation fees</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">3. Refunds</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Monthly Plans:</p>
                      <p className="text-sm sm:text-base ml-2">
                        No refunds for partial months. Access continues until the end of the paid period.
                      </p>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Annual Plans:</p>
                      <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                        <li>Refunds possible within 7 days of first purchase or renewal</li>
                        <li>Must have no material usage (no new tests or candidate screenings created)</li>
                        <li>Refund is pro-rated for the remaining unused term</li>
                        <li>Processing time: 5-10 business days after approval</li>
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Service Credits:</p>
                      <p className="text-sm sm:text-base ml-2">
                        Downtime credits available if monthly uptime &lt;99.9% (per SLA), provided as service credits, not cash refunds.
                      </p>
                    </div>
                    <div>
                      <p className="text-sm sm:text-base font-medium mb-2">Billing Errors:</p>
                      <p className="text-sm sm:text-base ml-2">
                        Duplicate charges or billing errors will be fully refunded once verified. Contact us immediately if you notice any discrepancies.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">4. Enterprise & Invoiced Customers</h3>
                  <p className="text-sm sm:text-base">
                    Terms of signed enterprise agreements override this policy if inconsistent. Please refer to your specific
                    contract for cancellation and refund terms.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">5. How to Cancel or Request a Refund</h3>
                  <div className="space-y-3">
                    <p className="text-sm sm:text-base">
                      <strong>Contact Methods:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                      <li>Email: <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a> (from admin email)</li>
                      <li>In-app billing portal: Go to Settings → Billing → Cancel Subscription</li>
                    </ul>
                    <p className="text-sm sm:text-base mt-3">
                      <strong>Required Information:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                      <li>Workspace name or account email</li>
                      <li>Plan & term (monthly/annual)</li>
                      <li>Order/Invoice ID</li>
                      <li>Reason for cancellation/refund request</li>
                    </ul>
                    <p className="text-sm sm:text-base mt-3">
                      <strong>Response Time:</strong> We typically respond within 2 business days.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">6. Chargebacks</h3>
                  <p className="text-sm sm:text-base">
                    Please contact VetriaAI before initiating chargebacks. We're committed to resolving billing issues amicably.
                    Unwarranted chargebacks may lead to account suspension. If you have concerns, reach out to our support team first.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">7. Refund Processing</h3>
                  <p className="text-sm sm:text-base mb-2">
                    Approved refunds will be processed to the original payment method:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Credit/Debit Cards: 5-10 business days</li>
                    <li>Bank Transfers: 7-14 business days</li>
                    <li>UPI: 3-5 business days</li>
                  </ul>
                  <p className="text-sm sm:text-base mt-3">
                    Refunds are subject to applicable taxes and payment gateway fees as per Indian regulations.
                  </p>
                </div>

                <div className="bg-primary/5 border-l-4 border-primary p-4 sm:p-6 rounded-r-lg">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">Contact Information</h3>
                  <p className="text-sm sm:text-base mb-2">
                    For cancellation or refund requests:
                  </p>
                  <p className="text-sm sm:text-base mb-2">
                    <strong>Email:</strong> <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>
                  </p>
                  <p className="text-sm sm:text-base">
                    <strong>Address:</strong> H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                    K.v.rangareddy, Balanagar, Telangana, India, 500018
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 px-4 sm:px-6 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-400">
            <p className="text-sm sm:text-base">&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CancellationsRefunds;
